// ─────────────────────────────────────────────
//  soloRelief — pure logic for "Solo" MD relief.
//
//  A Solo relief means one MD covers a room alone (no CRNA). While that solo
//  case is live, the MD cannot supervise their OTHER rooms — every one of
//  those cards shows an "MD unavailable" warning in the unified message slot
//  (with a Reassign MD action). The warning clears by itself when the solo
//  case actually ENDS: its room is cancelled, or it leaves the board (the ✕
//  clear deletes the assignment row; the midnight import resets the day).
//
//  Deliberately NOT used as end signals:
//   • the scheduled end time — real cases run late, and a late case is
//     exactly when the MD is still stuck in the room;
//   • status "empty" — sheet-imported boards leave every room's status
//     "empty" all day even with a full team assigned, so it means nothing.
// ─────────────────────────────────────────────

// "HH:MM" / "H:MM" → minutes since midnight, or null when unparseable.
export function timeToMinutes(t) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

// Match an assignment row's orRoom back to its live room doc (orRoom drops the
// "OR " prefix the way createAssignment stores it) — same rule as the board.
const roomForRow = (rooms, row) =>
  (Array.isArray(rooms) ? rooms : []).find(
    (r) => r && (r.name === row.orRoom || String(r.name || "").replace(/^OR\s+/i, "") === row.orRoom)
  ) || null;

// The one status that means the solo case is over while its row still exists.
const ENDED_STATUSES = new Set(["cancelled"]);

// The warning for ONE card: is this row's supervising MD tied up in a solo
// case elsewhere? Returns { roomName, startTime, active } or null.
//   assignment      — this card's assignment row
//   allAssignments  — every row on today's board (same facility)
//   rooms           — live room docs (to read the solo room's status)
export function soloMdConflict(assignment, allAssignments = [], rooms = []) {
  const myMd = assignment?.attendingId;
  // The solo room itself never warns about its own MD.
  if (!myMd || assignment?.soloMd) return null;
  const soloRow = (Array.isArray(allAssignments) ? allAssignments : []).find(
    (a) => a && a.soloMd && a.attendingId === myMd && a.id !== assignment.id
  );
  if (!soloRow) return null;
  const soloRoom = roomForRow(rooms, soloRow);
  if (soloRoom && ENDED_STATUSES.has(soloRoom.status)) return null;
  return {
    roomName: soloRow.orRoom || soloRoom?.name || "another room",
    startTime: soloRow.startTime || "",
    active: soloRoom?.status === "active",
  };
}
